import { useCallback, useState } from 'react';

import { useGetShippingLabelQuery, usePrintLabelMutation } from '@api/shippingLabel';
import { toast } from 'sonner';
import { useCheckPrinterStatusQuery } from '@api/connectInstance';

import { isConnectionError } from '@utils/helpers';

import { useRDInstance } from './useRdInstanceNew';

export const usePrint = () => {
  const [printError, setPrintError] = useState<string | null>();

  const { myConnectedInstance, isLoading, refresh } = useRDInstance();

  const [printLabel, { error: printLabelError, reset: resetPrintLabel }] = usePrintLabelMutation();

  const {
    data,
    refetch,
    isLoading: isLoadingTemplateData,
  } = useGetShippingLabelQuery(undefined, { skip: !myConnectedInstance?.id });

  const {
    data: PrinterStatus,
    error: printerStatusError,
    refetch: refetchPrinterStatus,
  } = useCheckPrinterStatusQuery(
    { name: data?.defaultPrinterName || '', connectionId: myConnectedInstance?.id ? `${myConnectedInstance?.id}` : undefined },
    { skip: !data || !myConnectedInstance?.id },
  );

  if (!printError && data?.defaultPrinterName && (printerStatusError || (PrinterStatus && !PrinterStatus.isActive))) {
    setPrintError(data?.defaultPrinterName);
  }

  const print = useCallback(async (pieceId?: number) => {
    if (!myConnectedInstance?.id || !data?.defaultPrinterName || !pieceId || printError) return;
    try {
      const result = await printLabel({ id: pieceId, rdConnectInstanceId: myConnectedInstance.id });
      if ('data' in result) {
        if (result.data?.objectData.success) {
          toast.success('Print request sent');
        } else {
          toast.error(result.data?.objectData.message || 'Something wrong with printer');
          setPrintError(data?.defaultPrinterName);
        }
      }
    } catch (error) {
      toast.error('Error while printing');
    }
  }, [data?.defaultPrinterName, myConnectedInstance?.id, printLabel, printError, setPrintError]);

  const refreshPrinter = async () => {
    const response = await refetch();
    if (!response.error && response.data?.defaultPrinterName) {
      const printerStatus = await refetchPrinterStatus();
      if (printerStatus.isError || !printerStatus.data?.isActive) {
        setPrintError(data?.defaultPrinterName);
      } else {
        setPrintError(null);
      }
    }
  };

  const instanceError = isConnectionError(printLabelError);

  const refreshInstannces = () => {
    refresh();
    resetPrintLabel();
  };

  return {
    isConfigError: (!data?.defaultPrinterName || !data.template) && !isLoadingTemplateData,
    print,
    isLoading,
    instanceError,
    refetchInstances: refreshInstannces,
    refreshPrinter,
    printError,
    currentPrinter: data?.defaultPrinterName,
  };
};
